import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { getSwapAffectedCount, swapMaterials, getSwapMaterialsList } from '../../../actions/inventory.action';
import { errorSnackBar } from '../../../actions/common.action';
import ConfirmSwapModal from './confirm-modal/index';
import './index.scss';
import { PerPageCount } from "../../../util/enums";
import MaterialSelectionPanel from './material-selection-panel/index';

export default function SwapMaterial() {
  const dispatch = useDispatch();
  const ITEMS_PER_PAGE = PerPageCount.DEFAULT;

  // Source State
  const [searchFrom, setSearchFrom] = useState('');
  const [searchFromQuery, setSearchFromQuery] = useState('');
  const [sourceMaterials, setSourceMaterials] = useState([]);
  const [sourceTotal, setSourceTotal] = useState(0);
  const [sourcePage, setSourcePage] = useState(1);
  const [sourceLoading, setSourceLoading] = useState(true);
  const [selectedFrom, setSelectedFrom] = useState(null);

  // Target State
  const [searchTo, setSearchTo] = useState('');
  const [searchToQuery, setSearchToQuery] = useState('');
  const [targetMaterials, setTargetMaterials] = useState([]);
  const [targetTotal, setTargetTotal] = useState(0);
  const [targetPage, setTargetPage] = useState(1);
  const [targetLoading, setTargetLoading] = useState(true);
  const [selectedTo, setSelectedTo] = useState(null);

  // Shared State
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [affectedCounts, setAffectedCounts] = useState({ taskTemplates: 0, itemTemplates: 0, total: 0 });
  const [swapping, setSwapping] = useState(false);

  // Data Fetching Logic
  const fetchMaterials = async (type, page, search) => {
    const setLoading = type === 'source' ? setSourceLoading : setTargetLoading;
    const setMaterials = type === 'source' ? setSourceMaterials : setTargetMaterials;
    const setTotal = type === 'source' ? setSourceTotal : setTargetTotal;

    setLoading(true);
    try {
      const offset = (page - 1) * ITEMS_PER_PAGE;
      const response = await dispatch(getSwapMaterialsList({
        search,
        offset,
        limit: ITEMS_PER_PAGE,
        page
      }));

      setMaterials(response?.materials || []);
      setTotal(response?.total || 0);
    } catch (error) {
      dispatch(errorSnackBar(`Failed to load ${type} materials`));
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    fetchMaterials('source', sourcePage, searchFromQuery);
  }, [sourcePage, searchFromQuery]);

  useEffect(() => {
    fetchMaterials('target', targetPage, searchToQuery);
  }, [targetPage, searchToQuery]);

  // Selection Logic
  const handleSelect = (material, type) => {
    if (type === 'source') {
      setSelectedFrom(prev => (prev?.id === material.id ? null : material));
    } else {
      setSelectedTo(prev => (prev?.id === material.id ? null : material));
    }
  };

  // Swap Logic
  const handleSwapClick = async () => {
    if (!selectedFrom || !selectedTo) return;
    if (selectedFrom.id === selectedTo.id) {
      dispatch(errorSnackBar('Cannot swap a material with itself'));
      return;
    }

    try {
      setSwapping(true);
      setShowConfirmModal(true);
      const response = await dispatch(getSwapAffectedCount({ sourceMaterialId: selectedFrom.id }));
      setAffectedCounts(response || { taskTemplates: 0, itemTemplates: 0, total: 0 });
    } catch (error) {
      dispatch(errorSnackBar('Failed to get affected count'));
    } finally {
      setSwapping(false);
    }
  };

  const handleConfirmSwap = async () => {
    const previousSource = selectedFrom;
    const previousTarget = selectedTo;

    // Optimistic Updates
    setShowConfirmModal(false);
    setSelectedFrom(null);
    setSelectedTo(null);

    try {
      setSwapping(true);
      await dispatch(swapMaterials({
        sourceMaterialId: previousSource.id,
        targetMaterialId: previousTarget.id
      }));
      setSearchFromQuery('');
      setSearchToQuery('');
      setSearchFrom('');
      setSearchTo('');
      setSourcePage(1);
      setTargetPage(1);

    } catch (error) {
      dispatch(errorSnackBar('Swap failed. Please try again.'));
      setSelectedFrom(previousSource);
      setSelectedTo(previousTarget);
      setShowConfirmModal(true);
    } finally {
      setSwapping(false);
    }
  };

  const canSwap = selectedFrom && selectedTo && selectedFrom.id !== selectedTo.id;
  const sameMaterialSelected = selectedFrom && selectedTo && selectedFrom.id === selectedTo.id;

  return (
    <div className="swap-materials-container pt-2">
      <div className="row g-3 h-100">

        {/* Source Column */}
        <div className="col-12 col-md">
          <MaterialSelectionPanel
            type="source"
            title="Source Material"
            subTitle="Material to be replaced"
            materials={sourceMaterials}
            total={sourceTotal}
            page={sourcePage}
            loading={sourceLoading}
            searchQuery={searchFrom}
            selectedMaterial={selectedFrom}
            itemsPerPage={ITEMS_PER_PAGE}
            onSearchChange={setSearchFrom}
            onSearchSubmit={() => {
              setSourcePage(1);
              setSearchFromQuery(searchFrom);
            }}
            onPageChange={setSourcePage}
            onSelect={(m) => handleSelect(m, 'source')}
            onClear={() => setSelectedFrom(null)}
          />
        </div>

        {/* Center Action Column */}
        <div className="col-12 col-md-auto d-flex flex-column align-self-center align-items-center justify-content-center my-2 my-md-0 px-2">
          <div className="text-center">
            <button
              data-cy="initiate-swap-btn"
              className={`btn btn-lg swap-button rounded-circle shadow ${canSwap ? 'btn-swap-gradient' : 'btn-secondary'}`}
              onClick={handleSwapClick}
              disabled={!canSwap || swapping}
              title={canSwap ? 'Click to swap materials' : sameMaterialSelected ? 'Cannot swap same material' : 'Select materials from both sides'}
            >
              <SwapHorizIcon className="swap-icon" />
            </button>
            {canSwap ? (
              <div className="small text-primary mt-2 fw-bold">Ready to Swap</div>
            ) : sameMaterialSelected ? (
              <div className="small text-warning mt-2 fw-bold">Materials Identical</div>
            ) : (
              <div className="small text-muted mt-2">Select both sides</div>
            )}
          </div>
        </div>

        {/* Target Column */}
        <div className="col-12 col-md">
          <MaterialSelectionPanel
            type="target"
            title="Target Material"
            subTitle="Material to replace with"
            materials={targetMaterials}
            total={targetTotal}
            page={targetPage}
            loading={targetLoading}
            searchQuery={searchTo}
            selectedMaterial={selectedTo}
            itemsPerPage={ITEMS_PER_PAGE}
            onSearchChange={setSearchTo}
            onSearchSubmit={() => {
              setTargetPage(1);
              setSearchToQuery(searchTo);
            }}
            onPageChange={setTargetPage}
            onSelect={(m) => handleSelect(m, 'target')}
            onClear={() => setSelectedTo(null)}
          />
        </div>

      </div>

      <ConfirmSwapModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSwap}
        sourceMaterial={selectedFrom}
        targetMaterial={selectedTo}
        affectedCounts={affectedCounts}
        loading={swapping}
      />
    </div>
  );
}